import { useState } from 'react'
import { createFileRoute, Link } from '@tanstack/react-router'
import { ClipboardCheck, Plus, Search, Ship } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { requireFlow } from '@/features/auth/guards'
import { NewInspectionDialog } from '@/features/inspection/components/new-inspection-dialog'
import { useInspections } from '@/features/inspection/hooks'
import { ENGINE_MANUFACTURERS } from '@/features/inventory/boat-form'
import { assetUrl } from '@/lib/api'

export const Route = createFileRoute('/_authenticated/inspection/')({
  beforeLoad: requireFlow('inspection'),
  component: InspectionPage,
})

const ENGINE_LABELS = Object.fromEntries(ENGINE_MANUFACTURERS.map((m) => [m.value, m.label]))

function InspectionPage() {
  const [search, setSearch] = useState('')
  const [createOpen, setCreateOpen] = useState(false)
  // The intake pool: only inspections not yet attached to a work order.
  const { data: inspections, isLoading } = useInspections({
    unattached: true,
    ...(search ? { q: search } : {}),
  })

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="flex items-center gap-2 text-3xl font-bold tracking-tight">
            <ClipboardCheck className="h-8 w-8" /> Inspection
          </h1>
          <p className="text-muted-foreground">
            Guided boat intake: scan the HIN, confirm engine details, photograph the boat.
          </p>
        </div>
        <Button onClick={() => setCreateOpen(true)}>
          <Plus className="mr-2 h-4 w-4" /> New Inspection
        </Button>
      </div>

      <div className="flex items-center gap-2">
        <div className="relative w-full max-w-sm">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search HIN, make, or model…"
            className="pl-8"
          />
        </div>
      </div>

      {isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-14 w-full" />
          ))}
        </div>
      ) : !inspections || inspections.length === 0 ? (
        <div className="flex flex-col items-center justify-center gap-2 rounded-lg border border-dashed p-12 text-center">
          <Ship className="h-10 w-10 text-muted-foreground" />
          <p className="font-medium">
            {search ? 'No inspections match your search.' : 'No unattached inspections.'}
          </p>
          {!search && (
            <p className="text-sm text-muted-foreground">
              Inspections stay on this list until they&rsquo;re attached to a work order.
            </p>
          )}
          {!search && (
            <Button className="mt-2" onClick={() => setCreateOpen(true)}>
              <Plus className="mr-2 h-4 w-4" /> New Inspection
            </Button>
          )}
        </div>
      ) : (
        <div className="rounded-lg border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Boat</TableHead>
                <TableHead>HIN</TableHead>
                <TableHead>Engine</TableHead>
                <TableHead className="text-right">Inspected</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {inspections.map((insp) => {
                const boat = insp.boat
                return (
                  <TableRow key={insp.id}>
                    <TableCell>
                      <Link
                        to="/inspection/$inspectionId"
                        params={{ inspectionId: insp.id }}
                        className="flex items-center gap-3 font-medium hover:underline"
                      >
                        {boat?.cover_thumb_url ? (
                          <img
                            src={assetUrl(boat.cover_thumb_url)}
                            alt=""
                            className="h-10 w-14 rounded-sm border object-cover"
                          />
                        ) : (
                          <span className="flex h-10 w-14 items-center justify-center rounded-sm border bg-muted">
                            <Ship className="h-4 w-4 text-muted-foreground" />
                          </span>
                        )}
                        <span>
                          {insp.year} {insp.make} {insp.model}
                        </span>
                      </Link>
                    </TableCell>
                    <TableCell className="font-mono text-xs tracking-wider">{insp.hin}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {insp.engine_count > 0 ? (
                        <>
                          {insp.engine_count} ×{' '}
                          {insp.engine_brand
                            ? (ENGINE_LABELS[insp.engine_brand] ?? insp.engine_brand)
                            : 'engine'}
                          {insp.engine_model ? ` ${insp.engine_model}` : ''}
                        </>
                      ) : (
                        'Unpowered'
                      )}
                    </TableCell>
                    <TableCell className="text-right text-sm text-muted-foreground">
                      {new Date(insp.performed_at).toLocaleDateString()}
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </div>
      )}

      <NewInspectionDialog open={createOpen} onOpenChange={setCreateOpen} />
    </div>
  )
}
